import { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useValidatePromo, calculateFinalPrice, useCreatePaymentIntent } from '@/hooks/useCheckout';
import { formatPrice } from '@/lib/stripe';
import { Loader2, Check, X, FileText, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CheckoutFormProps {
  articleId: string;
  articleTitle: string;
  basePrice: number; // Prix en centimes
  onSuccess: (purchaseId: string) => void;
  user?: { id: string; email: string; first_name?: string; last_name?: string } | null;
}

export function CheckoutForm({
  articleId,
  articleTitle,
  basePrice,
  onSuccess,
  user,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation('checkout');

  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<{
    discount: number;
    type: 'percent' | 'amount';
    id: string;
    code: string;
  } | null>(null);
  const [promoError, setPromoError] = useState('');

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Guest checkout fields (only used when user is not authenticated)
  const [guestEmail, setGuestEmail] = useState('');
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');

  // Champs de facturation (optionnels, deviennent requis si société renseignée)
  const [billingCompany, setBillingCompany] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingPostalCode, setBillingPostalCode] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingCountry, setBillingCountry] = useState('France');
  const [billingSiret, setBillingSiret] = useState('');
  const [billingVatNumber, setBillingVatNumber] = useState('');

  const validatePromo = useValidatePromo();
  const createPaymentIntent = useCreatePaymentIntent();

  const finalPrice = calculateFinalPrice(basePrice, appliedPromo || undefined);

  const handleApplyPromo = async () => {
    setPromoError('');
    try {
      const promo = await validatePromo.mutateAsync(promoCode);
      setAppliedPromo(promo);
    } catch (err: any) {
      setPromoError(err.message || 'Code promo invalide');
      setAppliedPromo(null);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    // Validation conditionnelle : si société renseignée, adresse requise
    if (billingCompany && (!billingAddress || !billingPostalCode || !billingCity)) {
      setError(t('form.billing.requiredWhenCompany'));
      setProcessing(false);
      return;
    }

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Step 1: Create PaymentIntent via Edge Function
      const { clientSecret, paymentIntentId } = await createPaymentIntent.mutateAsync({
        articleId,
        amount: finalPrice,
        promoCode: appliedPromo ? appliedPromo.code : undefined,
        promoCodeId: appliedPromo ? appliedPromo.id : undefined,
        // Guest checkout: pass email and name if not authenticated
        guestEmail: !user ? guestEmail : undefined,
        guestFirstName: !user ? guestFirstName : undefined,
        guestLastName: !user ? guestLastName : undefined,
        // Informations de facturation
        billingCompany: billingCompany || undefined,
        billingAddress: billingAddress || undefined,
        billingPostalCode: billingPostalCode || undefined,
        billingCity: billingCity || undefined,
        billingCountry: billingCountry || undefined,
        billingSiret: billingSiret || undefined,
        billingVatNumber: billingVatNumber || undefined,
      });

      // Step 2: Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent?.status === 'succeeded') {
        // Payment succeeded! Webhook will handle creating purchase record, PDF, and email
        onSuccess(paymentIntent.id);
      } else {
        throw new Error('Le paiement n\'a pas été confirmé');
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(
        err.message || 'Une erreur est survenue lors du paiement. Veuillez réessayer.'
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Article Summary */}
      <div className="bg-aspexit-gray-light rounded-lg p-6">
        <h3 className="font-semibold text-aspexit-black mb-2">Récapitulatif</h3>
        <div className="flex justify-between items-center">
          <span className="text-aspexit-gray">{articleTitle}</span>
          <span className="font-semibold">{formatPrice(basePrice)}</span>
        </div>
        {appliedPromo && (
          <div className="flex justify-between items-center mt-2 text-aspexit-green">
            <span>
              Réduction ({appliedPromo.discount}
              {appliedPromo.type === 'percent' ? '%' : '€'})
            </span>
            <span>
              -{formatPrice(basePrice - finalPrice)}
            </span>
          </div>
        )}
        <div className="flex justify-between items-center mt-3 pt-3 border-t border-border">
          <span className="font-bold text-aspexit-black">Total</span>
          <span className="font-bold text-xl text-aspexit-green">
            {formatPrice(finalPrice)}
          </span>
        </div>
      </div>

      {/* Guest Checkout Fields */}
      {!user && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="votre.email@exemple.com"
              required
            />
            <p className="text-xs text-aspexit-gray">
              Vous recevrez le PDF et un lien pour créer votre mot de passe
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName">Prénom *</Label>
              <Input
                id="firstName"
                value={guestFirstName}
                onChange={(e) => setGuestFirstName(e.target.value)}
                placeholder="Jean"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Nom *</Label>
              <Input
                id="lastName"
                value={guestLastName}
                onChange={(e) => setGuestLastName(e.target.value)}
                placeholder="Dupont"
                required
              />
            </div>
          </div>
        </div>
      )}

      {/* Informations de facturation */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-aspexit-gray" />
          <h3 className="font-semibold text-aspexit-black text-sm">
            {t('form.billing.title')}
          </h3>
        </div>
        <p className="text-xs text-aspexit-gray -mt-2">
          {t('form.billing.subtitle')}
        </p>

        <div className="space-y-2">
          <Label htmlFor="billingCompany">{t('form.billing.company')}</Label>
          <Input
            id="billingCompany"
            value={billingCompany}
            onChange={(e) => setBillingCompany(e.target.value)}
            placeholder={t('form.billing.companyPlaceholder')}
          />
        </div>

        {billingCompany && (
          <>
            <div className="space-y-2">
              <Label htmlFor="billingAddress">{t('form.billing.address')} *</Label>
              <Input
                id="billingAddress"
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
                placeholder={t('form.billing.addressPlaceholder')}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="billingPostalCode">{t('form.billing.postalCode')} *</Label>
                <Input
                  id="billingPostalCode"
                  value={billingPostalCode}
                  onChange={(e) => setBillingPostalCode(e.target.value)}
                  placeholder="75001"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billingCity">{t('form.billing.city')} *</Label>
                <Input
                  id="billingCity"
                  value={billingCity}
                  onChange={(e) => setBillingCity(e.target.value)}
                  placeholder="Paris"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="billingCountry">{t('form.billing.country')}</Label>
              <Input
                id="billingCountry"
                value={billingCountry}
                onChange={(e) => setBillingCountry(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billingSiret">{t('form.billing.siret')}</Label>
              <Input
                id="billingSiret"
                value={billingSiret}
                onChange={(e) => setBillingSiret(e.target.value)}
                placeholder={t('form.billing.siretPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="billingVatNumber">{t('form.billing.vatNumber')}</Label>
              <Input
                id="billingVatNumber"
                value={billingVatNumber}
                onChange={(e) => setBillingVatNumber(e.target.value)}
                placeholder={t('form.billing.vatPlaceholder')}
              />
            </div>
          </>
        )}
      </div>

      {/* Promo Code */}
      <div className="space-y-2">
        <Label htmlFor="promo">Code promo (optionnel)</Label>
        <div className="flex gap-2">
          <Input
            id="promo"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            placeholder="AGRICULTEUR2024"
            disabled={!!appliedPromo}
            className="flex-1"
          />
          {appliedPromo ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleRemovePromo}
              className="gap-2"
            >
              <X className="w-4 h-4" />
              Retirer
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={handleApplyPromo}
              disabled={!promoCode || validatePromo.isPending}
            >
              {validatePromo.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Appliquer'
              )}
            </Button>
          )}
        </div>
        {promoError && (
          <p className="text-sm text-red-600">{promoError}</p>
        )}
        {appliedPromo && (
          <div className="flex items-center gap-2 text-sm text-aspexit-green">
            <Check className="w-4 h-4" />
            <span>Code promo appliqué avec succès !</span>
          </div>
        )}
      </div>

      {/* Card Element */}
      <div className="space-y-2">
        <Label>Informations de paiement</Label>
        <div className="border border-border rounded-md p-3 bg-white">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1a1a1a',
                  '::placeholder': {
                    color: '#9ca3af',
                  },
                },
                invalid: {
                  color: '#ef4444',
                },
              },
              hidePostalCode: false,
            }}
          />
        </div>
        <p className="text-xs text-aspexit-gray">
          Paiement sécurisé par Stripe. Vos informations bancaires ne sont jamais
          stockées sur nos serveurs.
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-aspexit-green hover:bg-aspexit-green-dark"
        size="lg"
      >
        {processing ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Traitement en cours...
          </>
        ) : (
          <>Payer {formatPrice(finalPrice)}</>
        )}
      </Button>

      <p className="text-xs text-center text-aspexit-gray">
        En cliquant sur "Payer", vous acceptez nos{' '}
        <a href="/cgv" className="text-aspexit-green hover:underline">
          Conditions Générales de Vente
        </a>
      </p>

      {/* Bloc de réassurance */}
      <div className="space-y-3 pt-4 border-t border-border">
        {/* Facture */}
        <div className="flex items-start gap-2 text-xs text-aspexit-gray">
          <FileText className="w-4 h-4 flex-shrink-0 mt-0.5 text-aspexit-green" />
          <p>{t('form.reassurance.invoice')}</p>
        </div>

        {/* Clause de non-diffusion */}
        <div className="flex items-start gap-2 text-xs text-aspexit-gray">
          <Shield className="w-4 h-4 flex-shrink-0 mt-0.5 text-aspexit-green" />
          <div>
            <p>{t('form.reassurance.nonDistribution')}</p>
            <p className="mt-1 italic">{t('form.reassurance.nonDistributionBuyForThird')}</p>
          </div>
        </div>
      </div>
    </form>
  );
}
